import axios, { CancelTokenSource } from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectPagination } from 'selectors/pagination'

type InfiniteScrollOptions = {
  /** Unique identifier for pagination state in Redux store */
  paginationId: string
  /** Type of pagination (used in Redux-ORM selector) */
  paginationType: string
  /** Action creator that fetches a page of data */
  fetchAction: (params: { page: number; cancelToken?: any }) => any
  /** Callback fired when an error occurs during fetching */
  onError?: (error: any) => void
  /** Whether fetching is enabled */
  enabled?: boolean
}

type UseInfiniteScrollReturn = {
  /** Array of record IDs from all loaded pages */
  recordIds: Array<number | string>
  /** Whether more pages are available (undefined if not yet determined) */
  hasMore: boolean | undefined
  /** Whether a page is currently being fetched */
  isLoading: boolean
  /** Whether the initial load is in progress (computed from data availability) */
  isInitialLoading: boolean
  /** Function to load the next page */
  loadMore: () => Promise<void>
  /** Function to reset pagination state */
  reset: () => void
  /** Current page number (undefined if not loaded) */
  currentPage: number | undefined
  /** Total number of pages (undefined if not loaded) */
  totalPages: number | undefined
  /** Total number of records (undefined if not loaded) */
  totalCount: number | undefined
}

/**
 * Hook for managing infinite scroll pagination with Redux-ORM integration.
 *
 * Uses undefined values to naturally indicate loading states, eliminating
 * race conditions between API responses and data processing. Automatically
 * handles request cancellation and cleanup.
 *
 * @param options Configuration object for infinite scroll behavior
 * @returns Object containing pagination state and control functions
 *
 * @example
 * ```tsx
 * const {
 *   recordIds,
 *   hasMore,
 *   isInitialLoading,
 *   loadMore
 * } = useInfiniteScroll({
 *   paginationId: 'users-active',
 *   paginationType: 'user',
 *   fetchAction: getUsersAction,
 *   onError: handleError
 * })
 * ```
 */
export const useInfiniteScroll = ({
  paginationId,
  paginationType,
  fetchAction,
  onError = () => {},
  enabled = true,
}: InfiniteScrollOptions): UseInfiniteScrollReturn => {
  const dispatch = useDispatch()
  const [loadedPages, setLoadedPages] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<CancelTokenSource | null>(null)

  const paginationInfo = useSelector(ormState =>
    selectPagination(ormState, paginationType as any, paginationId)
  )

  // Check if there's existing data
  const hasExistingData =
    paginationInfo?.record_ids &&
    Object.keys(paginationInfo.record_ids).length > 0

  const currentPage = paginationInfo?.current_page
  const totalPages = paginationInfo?.total_pages
  const totalCount = paginationInfo?.total_count

  const recordIds = useMemo(() => {
    if (!paginationInfo?.record_ids) return []

    const result = loadedPages
      .sort((a, b) => a - b)
      .flatMap(page => paginationInfo.record_ids[page] ?? [])

    return result
  }, [loadedPages, paginationInfo?.record_ids])

  // With undefined values, we can determine loading state from data availability
  const isInitialLoading = totalCount === undefined && recordIds.length === 0

  const hasMore =
    currentPage !== undefined && totalPages !== undefined
      ? currentPage < totalPages
      : undefined

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.cancel('Request was canceled due to reset.')
      abortControllerRef.current = null
    }
    setLoadedPages([])
    setIsLoading(false)
  }, [])

  const paginationKeyRef = useRef({
    paginationId,
    paginationType,
  })

  useEffect(() => {
    const previous = paginationKeyRef.current
    if (
      previous.paginationId !== paginationId ||
      previous.paginationType !== paginationType
    ) {
      paginationKeyRef.current = { paginationId, paginationType }
      reset()
    }
  }, [paginationId, paginationType, reset])

  const fetchPage = useCallback(
    async (page: number) => {
      if (!enabled) return

      if (abortControllerRef.current) {
        abortControllerRef.current.cancel(
          'Request was canceled due to new one.'
        )
      }

      const cancelTokenSource = axios.CancelToken.source()
      abortControllerRef.current = cancelTokenSource

      try {
        setIsLoading(true)

        const response = await dispatch(
          fetchAction({
            page,
            cancelToken: cancelTokenSource.token,
          })
        )

        // Any cancel this hook issued (new request, reset, unmount) comes back
        // as a resolved error, not a failure the caller should surface.
        if (cancelTokenSource.token.reason) {
          return
        }

        if (response?.error) {
          onError(response.error)
          return
        }

        setLoadedPages(prev => {
          if (prev.includes(page)) return prev
          return [...prev, page].sort((a, b) => a - b)
        })
      } catch (error) {
        if (axios.isCancel(error)) {
          return
        }
        onError(error)
      } finally {
        // A superseded request must not clear the ref the live one now holds,
        // or the next reset/unmount can't cancel it.
        if (abortControllerRef.current === cancelTokenSource) {
          setIsLoading(false)
          abortControllerRef.current = null
        }
      }
    },
    [dispatch, fetchAction, onError, enabled]
  )

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading || !enabled || currentPage === undefined) return

    const nextPage = currentPage + 1
    await fetchPage(nextPage)
  }, [hasMore, isLoading, currentPage, fetchPage, enabled])

  useEffect(() => {
    if (!hasExistingData && enabled && loadedPages.length === 0) {
      // Need to fetch initial data
      fetchPage(1)
    }
  }, [enabled, hasExistingData, loadedPages.length, fetchPage])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.cancel('Component unmounted.')
      }
    }
  }, [])

  return {
    recordIds,
    hasMore,
    isLoading,
    isInitialLoading,
    loadMore,
    reset,
    currentPage,
    totalPages,
    totalCount,
  }
}

export default useInfiniteScroll
